import React, { Fragment, useState } from "react";

// Apollo Client
import { gql, useMutation, useQuery } from "@apollo/client";
import { uploadClient } from "../..";

// Material-UI Components
import Badge from "@material-ui/core/Badge";

// External Libraries
import deepEqual from "fast-deep-equal";
import { sort } from "fast-sort";
import { v4 } from "uuid";

// React Router
import { useHistory, useParams } from "react-router-dom";

// Redux
import { useDispatch, useSelector } from "react-redux";

// Redux Actions
import {
  toggleMailingDrawer,
  toggleTemplateDrawer,
} from "../../common/UserDataReducer";
import { compose } from "redux";

// Custom Hooks
import { useImmer } from "use-immer";

// Local Assets
import moreFilter from "../../assets/icons/filter.svg";
import calender from "../../assets/icons/calender-icon.svg";
import chevronRightImage from "../../assets/icons/chevron-right.svg";
import fileIcon from "../../assets/icons/file-icon.svg";
import userAvatar from "../../assets/icons/users-avatar.svg";

// Helper Functions
import { deepCopy, formatISODateToLongDate } from "../../helper";

// Custom Components
import Spinner from "react-bootstrap/Spinner";
import GenericTable from "../GenericTable/GenericTable";
import { notify } from "../Notify/Notify";
import MailingListDrawer from "../Request/Drawers/MailingListDrawer";
import TemplateDrawer from "../Request/Drawers/TemplateDrawer";
import useMailingListForm, {
  RESET_MAILING_LIST_FORM,
} from "../Request/Drawers/hooks/useMailingListForm";
import useTemplateForm, {
  RESET_TEMPLATE_FORM_STATE,
} from "../Request/Drawers/hooks/useTemplateForm";
import RequestMutationPopup from "../Request/Popups/RequestMutationPopup/RequestMutationPopup";
import useRequestMutationForm, {
  SET_ENTIRE_STATE,
} from "../Request/Popups/RequestMutationPopup/hooks/useRequestMutationForm";
import StatusChip from "../Request/RightSection/Table/StatusChip/StatusChip";
import useRequestDetailedViewTable from "../Request/RightSection/Table/hooks/useRequestDetailedViewTable";
import WarningModal from "../WarningModal/WarningModal";
import MoveSubmissions from "./Popups/MoveSubmissions/MoveSubmissions";
import RequestDetailedAdvanceFilter from "./RequestDetailedAdvanceFilter/RequestDetailedAdvanceFilter";

// Local Stylesheet
import "./RequestDetailedView.scss";
import { generateErrorMessageForCancelledRequest } from "../Request/RightSection/RightSection";
import startOfDay from "date-fns/startOfDay";
import parseISO from "date-fns/parseISO";
import endOfDay from "date-fns/endOfDay";
import isAfter from "date-fns/isAfter";

// Graphql Related
export const GET_REQUEST_DETAILS_BY_ID = gql`
  query GetRequestDetailsById($id: String!) {
    data: documentrequestById(id: $id) {
      id
      title
      dueDate
      reminders
      recurringRequest: recurringInterval
      emailDescription
      status
      documentsCount
      isTemplateModified: templateModified
      isMailingListModified: mailingListModified
      currentSelectTemplate: documentRequestTemplate {
        id
        title
      }
      templateModified
      currentSelectMailingList: mailingList {
        id
        title: name
      }
      userCount: mailingListUsersCount
      submittedBy: doneStatusUsersCount
      requiredDocs: documentRequestDocuments {
        documentId: id
        documentName
        document
      }
      users: mailingListUsers {
        id
        name
        email
        lastReminderSent
        documentsCount: totalSubmissionsCount
        submittedDocumentsCount: submittedCount
        status: submissionStatus
        fileMoved: submissionsMoved
      }
    }
  }
`;

const CANCEL_REQUEST_OF_USERS = gql`
  mutation CancelRequestOfUsers($documentRequestUserIds: [String]!) {
    CancelUsersDocumentRequest(
      documentRequestUserIds: $documentRequestUserIds
    ) {
      success
    }
  }
`;

export const SEND_DOCUMENT_REQUEST_SUBMISSION_REMINDER = gql`
  mutation SendDocumentRequestSubmissionReminder(
    $documentRequestUserIds: [String]!
  ) {
    data: sendDocumentRequestSubmissionReminder(
      documentRequestUserIds: $documentRequestUserIds
    ) {
      success
    }
  }
`;

const initialStateOfAdvanceFilter = {
  statusFilters: {
    done: false,
    pending: false,
    cancelled: false,
    expired: false,
  },
  fileMoved: {
    yes: false,
    no: false,
  },
};

function RequestDetailedView() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { requestId } = useParams();
  const {
    loading: isRequestDataLoading,
    data: { data: requestData = {} } = {},
  } = useQuery(GET_REQUEST_DETAILS_BY_ID, {
    client: uploadClient,
    variables: { id: requestId },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });
  const [isRequestMutationPopupOpen, setIsRequestMutationPopupOpen] =
    useState(false);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentAdvanceFilter, setCurrentAdvanceFilter] = useImmer(() =>
    deepCopy(initialStateOfAdvanceFilter)
  );
  const [finalAdvanceFilter, setFinalAdvanceFilter] = useImmer(() =>
    deepCopy(initialStateOfAdvanceFilter)
  );

  const {
    table,
    getAllSelectedRowIds,
    globalFilter,
    setGlobalFilter,
    pageIndex,
    resetAllRowSelection,
    getAllSelectedRows,
  } = useRequestDetailedViewTable(
    isRequestDataLoading
      ? Array.from({ length: 10000 })
      : filterTableData(
          requestData?.users?.map((user, index) => ({
            ...user,
            fileMoved: user?.fileMoved ? "Yes" : "No",
          })) ?? [],
          finalAdvanceFilter,
          requestData?.status?.toLowerCase() === "pending",
          requestId
        )
  );
  const { state, onChange } = useRequestMutationForm();

  const isAtLeastOneRowSelected = getAllSelectedRowIds().length > 0;
  const handleOpenRequestMutation = () => {
    const mutationState = transformStateForEditRequest(requestData ?? {}) ?? {};
    onChange(SET_ENTIRE_STATE, {
      ...deepCopy(mutationState),
      initialStateForEdit: deepCopy(mutationState),
    });
    setIsRequestMutationPopupOpen(true);
  };
  // Advance search related
  const handleClickOnAdvanceSearch = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAdvanceFilterChange = (key, value) => {
    setCurrentAdvanceFilter((draft) => {
      if (key === "clearAllFilters") {
        return deepCopy(initialStateOfAdvanceFilter);
      }
      if (key === "statusFilters") {
        if (value === "selectAllStatus") {
          const allFiltersValue = Object.keys(draft.statusFilters).some(
            (filter) => draft.statusFilters[filter] === false
          );
          Object.keys(draft.statusFilters).forEach((filter) => {
            draft.statusFilters[filter] = allFiltersValue;
          });
        } else {
          draft.statusFilters[value] = !draft.statusFilters[value];
        }
      } else if (key === "fileMoved") {
        // Handle fileMoved filter
        if (value === "selectAllFileMoved") {
          const allFiltersValue = Object.keys(draft.fileMoved).some(
            (filter) => draft.fileMoved[filter] === false
          );
          Object.keys(draft.fileMoved).forEach((filter) => {
            draft.fileMoved[filter] = allFiltersValue;
          });
        } else {
          draft.fileMoved[value] = !draft.fileMoved[value];
        }
      }
      // Add more conditions for other filters if needed in the future

      // Additional logic for handling other filter types can be added here
    });
  };

  const handleClose = (isSave = false) => {
    if (isSave) {
      setFinalAdvanceFilter(currentAdvanceFilter);
    } else {
      setCurrentAdvanceFilter(deepCopy(finalAdvanceFilter));
    }
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  // Manage Move Submissions
  const [isMoveSubmissionPopupOpen, setIsMoveSubmissionPopupOpen] =
    useState(false);

  const [
    isWarningModelForMoveSubmissionOpen,
    setIsWarningModelForMoveSubmissionOpen,
  ] = useState(false);

  // Selected users
  const selectedUserIds = getAllSelectedRowIds();

  //  Manage Cancel Selected
  const [isRequestApprovalPopupOpen, setIsRequestApprovalPopupOpen] =
    useState(false);
  // Mutation
  const [cancelRequestOfUsers, { loading: isCancelRequestOfUsers }] =
    useMutation(CANCEL_REQUEST_OF_USERS, {
      client: uploadClient,
      refetchQueries: [
        { query: GET_REQUEST_DETAILS_BY_ID, variables: { id: requestId } },
      ],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Cancel Request Of Users Mutation Error:", error);
      },
      onCompleted: () => {
        notify("Success", "Requests are successfully canceled", "success");
        resetAllRowSelection();
        setIsRequestApprovalPopupOpen(false);
      },
      awaitRefetchQueries: true,
    });

  // Handle send reminder
  const [sendReminderToUsers, { loading: isSendReminderToUsersLoading }] =
    useMutation(SEND_DOCUMENT_REQUEST_SUBMISSION_REMINDER, {
      client: uploadClient,
      refetchQueries: [
        { query: GET_REQUEST_DETAILS_BY_ID, variables: { id: requestId } },
      ],
      onError: (error) => {
        notify("Error", "Something went wrong.", "error");
        console.error("Send Reminder to Users Mutation Error:", error);
      },
      onCompleted: () => {
        notify("Success", "Reminder(s) are successfully sent", "success");
        resetAllRowSelection();
      },
      awaitRefetchQueries: true,
    });

  // Handle Template Drawer
  const isTemplateDrawerOpen = useSelector(
    (state) => state.isTemplateDrawerOpen
  );
  const { templateState, onChange: onChangeForTemplate } = useTemplateForm();
  const templateOnCloseHandler = () => {
    compose(dispatch, toggleTemplateDrawer)(false);
    onChangeForTemplate(RESET_TEMPLATE_FORM_STATE);
  };

  // Handle Mailing List Drawer
  const { mailingListState, onChange: onChangeForMailingList } =
    useMailingListForm();
  const isMailingDrawerOpen = useSelector((state) => state.isMailingDrawerOpen);
  const mailingListOnCloseHandler = () => {
    compose(dispatch, toggleMailingDrawer)(false);
    onChangeForMailingList(RESET_MAILING_LIST_FORM);
  };

  const isEditRequestBtnDisabled =
    isRequestDataLoading ||
    ["DONE", "CANCELLED"].includes(requestData?.status?.toUpperCase());

  return (
    <Fragment>
      <main className="new-design-v1 request-detailed-view-main-container">
        <section className="request-detailed-header px-8">
          <div className="request-detailed-breadcrumb-option-wrapper">
            <div
              className="request-detailed-breadcrumb-option cursor-pointer"
              onClick={() => history.push("/requests")}
            >
              Requests
            </div>
            <div>
              <img src={chevronRightImage} alt="sep" />
            </div>
            <div className="request-detailed-breadcrumb-option">
              {requestData.title}
            </div>
          </div>
          <div className="request-detailed-status">
            {!isRequestDataLoading && (
              <Fragment>
                <div className="d-flex flex-row-reverse gap-8 w-100">
                  <div className="d-flex align-items-center ">
                    <img src={fileIcon} alt="fileIcon" />
                    <div className="pl-2">{`${
                      requestData?.documentsCount ?? ""
                    } Documents Requested`}</div>
                  </div>
                  <div className="d-flex align-items-center ">
                    <img src={calender} alt="calender" />
                    <div className="pl-2">{`Due on ${formatISODateToLongDate(
                      requestData?.dueDate
                    )}`}</div>
                  </div>
                  <div className="d-flex align-items-center ">
                    <img src={userAvatar} alt="userAvatar" />
                    <div className="pl-2">{`${
                      requestData?.userCount ?? ""
                    } Recipients - ${requestData?.submittedBy ?? ""} of ${
                      requestData?.userCount ?? ""
                    } Submissions`}</div>
                  </div>
                </div>
                <StatusChip
                  value={
                    isDueDatePast(
                      requestData.dueDate,
                      requestData?.status?.toLowerCase()
                    )
                      ? "expired"
                      : requestData?.status?.toLowerCase() ?? "pending"
                  }
                />
              </Fragment>
            )}

            {isRequestDataLoading && (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner animation="border" role="status" />
              </div>
            )}
          </div>
        </section>
        <section className="px-8 pt-8">
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div className="d-flex">
              <div className="search-box mr-4">
                <input
                  type="text"
                  maxLength="50"
                  placeholder="&#xF002;  Search Requests"
                  className="search-placeholder"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                />
              </div>
              <Badge
                color="secondary"
                variant={
                  deepEqual(
                    finalAdvanceFilter,
                    deepCopy(initialStateOfAdvanceFilter)
                  )
                    ? undefined
                    : "dot"
                }
              >
                <img
                  src={moreFilter}
                  alt="moreFilter"
                  className="more-filter-icon active"
                  onClick={handleClickOnAdvanceSearch}
                />
              </Badge>
              <RequestDetailedAdvanceFilter
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                state={currentAdvanceFilter}
                handleChange={handleAdvanceFilterChange}
              />
            </div>
            <div className="d-flex">
              {isAtLeastOneRowSelected && (
                <button
                  type="button"
                  className="secondary-button  mr-5"
                  style={{ minWidth: "140px" }}
                  data-dismiss="modal"
                  onClick={() => setIsWarningModelForMoveSubmissionOpen(true)}
                >
                  Move Selected{" "}
                  <em>{` (${getAllSelectedRowIds().length})`} </em>
                </button>
              )}
              {isAtLeastOneRowSelected && (
                <button
                  type="button"
                  className="secondary-button  mr-5"
                  style={{ minWidth: "190px" }}
                  data-dismiss="modal"
                  onClick={() => setIsRequestApprovalPopupOpen(true)}
                >
                  Cancel Selected{" "}
                  <em>{` (${getAllSelectedRowIds().length})`} </em>
                </button>
              )}

              {!isAtLeastOneRowSelected && (
                <button
                  type="button"
                  className="secondary-button mr-5"
                  data-dismiss="modal"
                  disabled={isEditRequestBtnDisabled}
                  onClick={handleOpenRequestMutation}
                >
                  Edit Request
                </button>
              )}
              {isAtLeastOneRowSelected &&
                requestData?.status?.toLowerCase() === "pending" && (
                  <button
                    className="primary-button"
                    style={{ minWidth: "260px" }}
                    disabled={isSendReminderToUsersLoading}
                    onClick={() => {
                      sendReminderToUsers({
                        variables: {
                          documentRequestUserIds: selectedUserIds,
                        },
                      });
                    }}
                  >
                    Send Reminder to Selected
                    <em>{` (${getAllSelectedRowIds().length})`} </em>
                  </button>
                )}
            </div>
          </div>
          <div className="mt-8">
            <GenericTable
              table={table}
              pageIndex={pageIndex}
              shouldEnablePagination={false}
              tableClassName="request-detailed-table-container"
              isLoading={isRequestDataLoading}
              noRecordFoundMsg={"No request found."}
            />
          </div>
        </section>
      </main>
      <RequestMutationPopup
        state={state}
        onChange={onChange}
        isEdit={true}
        isOpen={isRequestMutationPopupOpen}
        onClose={() => setIsRequestMutationPopupOpen(false)}
      />

      <WarningModal
        headingTitle="Cancelling Request"
        descriptionText="You are about to cancel an open request with pending documents. Doing so will mark the request as cancelled and prevent requestees from adding any further documents."
        isOpen={isRequestApprovalPopupOpen}
        onCloseHandler={(isProceed) => {
          if (isProceed) {
            const allSelectedRows = getAllSelectedRows();
            const cancelledRowSelected = allSelectedRows?.filter(
              (u) => u?.status === "CANCELLED"
            );
            if (cancelledRowSelected.length > 0) {
              const errorMsg = generateErrorMessageForCancelledRequest(
                allSelectedRows,
                cancelledRowSelected
              );
              notify("Error", errorMsg, "error");
              setIsRequestApprovalPopupOpen(false);
              return;
            }
            cancelRequestOfUsers({
              variables: {
                documentRequestUserIds: selectedUserIds,
              },
            });
          } else {
            setIsRequestApprovalPopupOpen(false);
          }
        }}
        isProceedBtnDisable={isCancelRequestOfUsers}
      />
      <WarningModal
        headingTitle="Requests in Pending State"
        descriptionText="You are about to move requests with open items still in pending state. Doing so will mark those requests as completed."
        isOpen={isWarningModelForMoveSubmissionOpen}
        onCloseHandler={async (isProceed) => {
          setIsWarningModelForMoveSubmissionOpen(false);
          if (isProceed) {
            setIsMoveSubmissionPopupOpen(true);
          }
        }}
      />
      <MoveSubmissions
        isOpen={isMoveSubmissionPopupOpen}
        onCloseHandler={() => setIsMoveSubmissionPopupOpen(false)}
        selectedUserIds={selectedUserIds}
      />
      <TemplateDrawer
        isOpen={isTemplateDrawerOpen}
        onClose={templateOnCloseHandler}
        isEdit={false}
        onChange={onChangeForTemplate}
        templateState={templateState}
      />
      <MailingListDrawer
        isOpen={isMailingDrawerOpen}
        onClose={mailingListOnCloseHandler}
        isEdit={false}
        onChange={onChangeForMailingList}
        mailingListState={mailingListState}
      />
    </Fragment>
  );
}

export default RequestDetailedView;

// Helper functions
/**
 * Filters and sorts table data based on the provided filters, pending request status, and request ID.
 *
 * @param {Array} data - The array of data to be filtered and sorted.
 * @param {object} filters - The filters object containing status filters.
 * @param {boolean} isPendingRequest - Indicates if the data is associated with a pending request.
 * @param {string} requestId - The ID of the associated request.
 *
 * @returns {Array} The filtered and sorted array of table data.
 */
function filterTableData(data = [], filters = {}, isPendingRequest, requestId) {
  return sort(
    data
      .filter((item) => {
        // Check if at least one status filter is selected
        const anyStatusFilterSelected = Object.values(
          filters.statusFilters
        ).some((filter) => filter);

        // Check if item matches any selected status filter (OR relation)
        const allStatusFiltersSelected = anyStatusFilterSelected
          ? Object.keys(filters.statusFilters).some(
              (filter) =>
                filters.statusFilters[filter] &&
                item.status.toUpperCase() === filter.toUpperCase()
            )
          : true;

        // Check if at least one option is selected for fileMoved
        const fileMovedFilters = Object.keys(filters.fileMoved);
        const anyFileMovedOptionSelected = fileMovedFilters.some(
          (filter) => filters.fileMoved[filter]
        );

        // Check if item matches any selected fileMoved options (OR relation)
        const allFileMovedOptionsSelected = anyFileMovedOptionSelected
          ? fileMovedFilters.some(
              (filter) =>
                filters.fileMoved[filter] &&
                item.fileMoved.toUpperCase() === filter.toUpperCase()
            )
          : true;

        // Return true only if item matches all selected root-level filters (statusFilters AND fileMoved)
        return allStatusFiltersSelected && allFileMovedOptionsSelected;
      })
      .map((u) => ({
        ...u,
        isOfPendingRequest: isPendingRequest,
        requestId: requestId,
      }))
  ).asc((u) => u?.name);
}

/**
 * Transforms data for editing a request by ensuring default values and preparing required structures.
 *
 * @param {object} data - The data object representing the request being edited.
 * @param {string} data.id - The ID of the request.
 * @param {string} data.title - The title of the request.
 * @param {string} data.dueDate - The due date of the request.
 * @param {string} data.reminders - The reminders for the request.
 * @param {string} data.recurringRequest - The recurring interval for the request.
 * @param {string} data.emailDescription - The email description for the request.
 * @param {boolean} data.isTemplateModified - Indicates if the template is modified.
 * @param {boolean} data.isMailingListModified - Indicates if the mailing list is modified.
 * @param {object} data.currentSelectTemplate - The selected template for the request.
 * @param {string} data.currentSelectTemplate.id - The ID of the selected template.
 * @param {string} data.currentSelectTemplate.title - The title of the selected template.
 * @param {Array} data.requiredDocs - An array of required documents for the request.
 * @param {Array} data.users - An array of users associated with the request.
 * @param {object} data.currentSelectMailingList - The selected mailing list for the request.
 * @param {string} data.currentSelectMailingList.id - The ID of the selected mailing list.
 * @param {string} data.currentSelectMailingList.title - The title of the selected mailing list.
 *
 * @returns {object} The transformed data object for editing the request.
 */
function transformStateForEditRequest(data) {
  const transformedData = {
    id: data.id ?? v4(),
    title: data.title ?? "",
    dueDate: data.dueDate ?? "",
    reminders: data.reminders ?? "Weekly",
    recurringRequest: data.recurringRequest ?? "",
    emailDescription: data.emailDescription ?? "",
    isTemplateModified: data.isTemplateModified ?? false,
    isMailingListModified: data.isMailingListModified ?? false,
    currentSelectTemplate: {
      id: data.currentSelectTemplate ? data.currentSelectTemplate.id : "",
      title: data.currentSelectTemplate ? data.currentSelectTemplate.title : "",
    },
    requiredDocs: [],
    currentSelectMailingList: {
      id: data.currentSelectMailingList ? data.currentSelectMailingList.id : "",
      title: data.currentSelectMailingList
        ? data.currentSelectMailingList.title
        : "",
    },
    requiredDocsFromTemplate: [],
    usersFromMailingList:
      sort(
        data?.users?.map(({ id, email, name }) => ({ id, email, name }))
      ).asc((u) => u?.name?.toLowerCase()) ?? [],
    users:
      sort(
        data?.users?.map(({ id, email, name }) => ({ id, email, name }))
      ).asc((u) => u?.name?.toLowerCase()) ?? [],
    initialStateForEdit: {},
  };

  // Handle requiredDocs transformation
  if (Array.isArray(data?.requiredDocs)) {
    transformedData.requiredDocs = sort(
      data.requiredDocs.map((doc) => ({
        documentId: doc.documentId,
        documentName: doc.documentName || "",
        documentFile: typeof doc?.document === "string" ? null : -1,
        documentFileName: doc?.document?.split("/")?.pop()?.trim(),
      }))
    ).asc((u) => u?.documentName?.toLowerCase());
    transformedData.requiredDocsFromTemplate = deepCopy(
      transformedData.requiredDocs
    );
  }

  return transformedData;
}

export function isDueDatePast(rawDueDate, status) {
  if (status?.toLowerCase() !== "pending") {
    return false;
  }
  try {
    const TODAY = startOfDay(parseISO(new Date().toISOString()));
    const dueDate = endOfDay(parseISO(rawDueDate));
    return isAfter(TODAY, dueDate);
  } catch (error) {
    return false;
  }
}
